/**
 * snap-delta — the SINGLE source of truth for the "week-over-week delta from
 * REAL snapshot history" computation (extracted from MainTrendTile in
 * quick-260701-dea Task 1 / FIX B so MainBucketedTile can reuse the EXACT
 * algorithm — one implementation, no drift).
 *
 * MainMetricResponse carries no prior-period field, so the only trustworthy
 * prior value is the snapshot series itself. We compare the latest snapshot to
 * the snapshot closest to 7 days earlier (clamped to the oldest point when the
 * history is shorter). Returns null when there is no genuine prior point — the
 * caller then renders NO chip (per the locked "no fabricated deltas" rule).
 */
import type { MainSnapResponse } from '@saledash/shared';

export interface SnapDelta {
  /** Signed fractional change, e.g. +0.04 = +4%. */
  ratio: number;
  /** True when the latest value rose vs the prior, false when it fell. */
  up: boolean;
  /** True when the two points are numerically equal (flat). */
  flat: boolean;
}

export function computeSnapDelta(
  snapshots: MainSnapResponse['snapshots'],
): SnapDelta | null {
  // Need at least two DISTINCT-dated points for a real comparison.
  const distinct = new Map<string, number>();
  for (const s of snapshots) distinct.set(s.snap_date, s.value);
  if (distinct.size < 2) return null;

  const points = [...distinct.entries()]
    .map(([date, value]) => ({ t: new Date(date).getTime(), value }))
    .filter((p) => !Number.isNaN(p.t))
    .sort((a, b) => a.t - b.t);
  if (points.length < 2) return null;

  const latest = points[points.length - 1];
  if (!latest) return null;

  // Target ~7 days before the latest snapshot; fall back to the oldest point
  // when the series is shorter than a week.
  const weekMs = 7 * 24 * 60 * 60 * 1000;
  const targetT = latest.t - weekMs;
  let prior = points[0];
  for (const p of points) {
    if (p === latest) break;
    if (p.t <= targetT) prior = p;
  }
  if (!prior || prior === latest) prior = points[0] ?? prior;
  if (!prior || prior.t === latest.t) return null;
  if (prior.value === 0) {
    // Can't express a percentage from a zero base — treat as no real delta.
    return latest.value === prior.value
      ? { ratio: 0, up: false, flat: true }
      : null;
  }

  const ratio = (latest.value - prior.value) / Math.abs(prior.value);
  if (ratio === 0) return { ratio: 0, up: false, flat: true };
  return { ratio, up: ratio > 0, flat: false };
}
